import { motion } from 'framer-motion';
import React, { useRef } from 'react';
import { Children, createContext, memo, useContext, useState, type ReactElement, type ReactNode } from 'react';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';

interface SlidePanelContextValue {
  value: string;
  activeIndex: number;
}

const SlidePanelContext = createContext<SlidePanelContextValue | null>(null);

function useSlidePanelContext() {
  const context = useContext(SlidePanelContext);

  if (!context) {
    throw new Error('SlidePanel components must be used within a SlidePanelContainer');
  }

  return context;
}

interface SlidePanelProps {
  name: string;
  children: ReactNode;
  className?: string;
  index?: number;
  onActivate?: (container: HTMLDivElement | null) => void;
}

export const SlidePanel = memo<SlidePanelProps>(({ name, children, className = '', index = 0, onActivate }) => {
  const { value, activeIndex } = useSlidePanelContext();
  const relativeIndex = index - activeIndex;
  const [visible, setVisible] = useState(relativeIndex === 0);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <motion.div
      ref={containerRef}
      className={classNames(className, 'absolute inset-0 flex flex-col', {
        hidden: !visible,
      })}
      transition={{ ease: cubicEasingFn }}
      initial={{
        opacity: value === name ? 1 : 0,
        x: `${relativeIndex * 100}%`,
      }}
      animate={{
        opacity: value === name ? 1 : 0,
        x: relativeIndex ? `${relativeIndex * 100}%` : '0%',
      }}
      onAnimationStart={() => {
        setVisible(true);
      }}
      onAnimationComplete={() => {
        setVisible(relativeIndex === 0);

        if (onActivate && relativeIndex === 0) {
          onActivate(containerRef.current);
        }
      }}
      tabIndex={relativeIndex === 0 ? 0 : -1}
    >
      {children}
    </motion.div>
  );
});

SlidePanel.displayName = 'SlidePanel';

interface SlidePanelContainerProps {
  value: string;
  children: ReactElement<SlidePanelProps> | ReactElement<SlidePanelProps>[];
  className?: string;
}

export const SlidePanelContainer = memo<SlidePanelContainerProps>(({ value, children, className = '' }) => {
  const childArray = Children.toArray(children);
  const activeIndex = childArray.findIndex((child) => (child as ReactElement<SlidePanelProps>).props.name === value);

  const indexedChildren = childArray.map((child, index) => {
    if (React.isValidElement<SlidePanelProps>(child)) {
      return React.cloneElement(child, {
        index,
      });
    }

    return child;
  });

  return (
    <SlidePanelContext.Provider value={{ value, activeIndex }}>
      <div className={`relative overflow-hidden ${className}`}>{indexedChildren}</div>
    </SlidePanelContext.Provider>
  );
});

SlidePanelContainer.displayName = 'SlidePanelContainer';
